import {
  BrandLelandPlusIcon,
  Button,
  ButtonColor,
  ButtonWidth,
  formatPrice,
  IconChevronDown,
  pluralize,
} from '@leland-dev/leland-ui-library';
import { AnimatePresence, domAnimation, LazyMotion, m } from 'framer-motion';
import React, { type FC, useMemo, useState } from 'react';

import {
  CoachVisibility,
  type Scalars,
} from '../../__generated-gql-types__/globalTypes';
import { useAuth } from '../../context/AuthContext';
import { categoryUrnToSlug } from '../../utils/category';
import { getCTATooltipTextForBlockedUser } from '../../utils/coach';
import {
  BASE_LELAND_PLUS_MARKETING_URL,
  LELAND_PLUS_REDIRECT_URL,
} from '../../utils/constants';
import useMediaQuery, { Breakpoints } from '../../utils/hooks/useMediaQuery';
import { isLiveLelandPlusCategory } from '../../utils/leland-plus';
import { useLoginModal } from '../LoginModal';
import CustomPackageWidget from '../package/CustomPackageWidget';
import OfferingPackageCard from '../package/OfferingPackageCard';

import OtherCategoriesText from './OtherCategoriesText';
import { ServicesDescription } from './ServicesDescription';
import {
  type CoachOfferingsCoachFragment,
  type CoachOfferingsCoachingFocusFragment,
  type CoachOfferingsOfferingPackageFragment,
  useCoachOfferingsSkusQuery,
  useCoachOfferingsUserQuery,
} from './__generated-gql-types__/CoachOfferings.generated';
import { useMessageModal } from './modals/MessageModal';

enum CTAType {
  CUSTOM_PACKAGE,
  BUY_COACHING,
}

interface CoachOfferingsProps {
  livePackages: CoachOfferingsOfferingPackageFragment[];
  coach: CoachOfferingsCoachFragment;
  categoryUrn: Scalars['CategoryUrn']['output'];
  services: Possible<CoachOfferingsCoachingFocusFragment[]>;
  onGetStarted: () => void;
  headline: string;
  largeHeadline?: boolean;
  blocked?: boolean;
}

const CoachOfferings: FC<CoachOfferingsProps> = ({
  livePackages,
  coach,
  categoryUrn,
  services,
  onGetStarted,
  headline,
  largeHeadline,
  blocked,
}) => {
  const { currentUser, isNewSubscriptionExperience } = useAuth();

  const isNewExperience =
    isNewSubscriptionExperience ||
    !!coach.newExperience ||
    coach.coachingRelationship?.newExperience;

  const isLoggedIn = !!currentUser?.applicant;

  const { setOpen: setLoginModalOpen, Modal: LoginModal } = useLoginModal();
  const { setOpen: setMessageModalOpen, Modal: MessageModal } =
    useMessageModal();

  const [collapsed, setCollapsed] = useState(true);

  const [CTAClicked, setCTAClicked] = useState<CTAType | null>(null);

  const { data: userData } = useCoachOfferingsUserQuery();

  const { data: skusData } = useCoachOfferingsSkusQuery({
    skip: !isNewExperience,
  });

  const coachStartingPrice = useMemo(
    () =>
      skusData?.skus.results.find((sku) => sku.tier === coach.tier)
        ?.unitPrice ?? null,
    [skusData, coach.tier],
  );

  const isLelandPlusSubscriber = userData?.user?.applicant?.hasLelandPlusAccess;

  const isLg = useMediaQuery(Breakpoints.LG);

  const gridSize = isLg ? 3 : 2;

  const featuredPackages = livePackages.slice(0, gridSize);
  const morePackages = livePackages.slice(gridSize);

  const otherCategories = coach?.categoryInformationList?.filter(
    (c) => c.category.id != categoryUrn,
  );

  const hasCollapsibleSection =
    featuredPackages.length === gridSize &&
    // CustomPackageWidget is shown if `isNewExperience` is false, hence the extra check
    (!isNewExperience || !!morePackages.length);

  const showOtherCategoriesText =
    otherCategories &&
    otherCategories.length > 0 &&
    !(collapsed && hasCollapsibleSection);

  const handleCustomPackageCTA = () => {
    setCTAClicked(CTAType.CUSTOM_PACKAGE);
    if (isLoggedIn) {
      setMessageModalOpen(true);
    } else {
      setLoginModalOpen(true);
    }
  };

  const handleBuyCoaching = () => {
    setCTAClicked(CTAType.BUY_COACHING);
    if (isLoggedIn) {
      onGetStarted();
    } else {
      setLoginModalOpen(true);
    }
  };

  const collapsibleGridClassNames =
    'grid xl:grid-cols-3 md:grid-cols-2 md:gap-4';

  const blockedUserCTAText = getCTATooltipTextForBlockedUser(
    coach.user.firstName,
  );

  const isLelandPlusExistingCategory = isLiveLelandPlusCategory(categoryUrn);

  const landingPageURL = `${BASE_LELAND_PLUS_MARKETING_URL}/${categoryUrnToSlug(
    categoryUrn,
  )}`;

  const getLandingPageUrl = (coachSlug: string) => {
    const baseUrl = new URL(landingPageURL);
    baseUrl.searchParams.set('utm_source', 'site');
    baseUrl.searchParams.set('utm_medium', 'coach');
    baseUrl.searchParams.set('utm_campaign', coachSlug);
    return baseUrl.toString();
  };

  const noContactForNewClient =
    coach.visibility === CoachVisibility.PRIVATE_CLOSED &&
    !coach.coachingRelationship;

  return (
    <LazyMotion strict features={domAnimation}>
      <h2
        className={`mb-2 text-left font-medium text-leland-gray-dark md:mb-8 ${largeHeadline ? 'text-2xl' : 'text-lg'}`}
      >
        {headline}
      </h2>
      {featuredPackages.length > 0 ? (
        <div className={collapsibleGridClassNames}>
          {featuredPackages.map((p) => (
            <OfferingPackageCard
              key={p.id}
              offeringPackage={p}
              opportunityCoach={coach}
            />
          ))}
          {!isNewExperience &&
          featuredPackages.length < gridSize &&
          !noContactForNewClient ? (
            <CustomPackageWidget
              onClick={handleCustomPackageCTA}
              coachFirstName={coach.user.firstName}
              blocked={blocked}
            />
          ) : null}
        </div>
      ) : null}

      {hasCollapsibleSection ? (
        <AnimatePresence initial={false}>
          {collapsed ? null : (
            <m.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className={`${collapsibleGridClassNames} mt-4`}>
                {morePackages.map((p) => (
                  <OfferingPackageCard
                    key={p.id}
                    offeringPackage={p}
                    opportunityCoach={coach}
                  />
                ))}
                {isNewExperience ? null : (
                  <CustomPackageWidget
                    onClick={handleCustomPackageCTA}
                    coachFirstName={coach.user.firstName}
                    blocked={blocked}
                  />
                )}
              </div>
            </m.div>
          )}
        </AnimatePresence>
      ) : null}

      {!noContactForNewClient && (!!coach.hourlyRate || isNewExperience) ? (
        <div className="mt-6 flex flex-col justify-between rounded-lg bg-leland-gray-hover p-4 md:mt-4 md:flex-row md:items-center md:space-x-4">
          <div className="flex flex-col items-start">
            <h2 className="line-clamp-1 text-left text-lg font-medium text-leland-gray-dark">
              {isNewExperience ? 'Pay as you go ' : 'Custom hourly coaching '}
              {isNewExperience && !coachStartingPrice ? null : (
                <>
                  <span className="mx-1">&middot;</span>{' '}
                  <span className="text-base font-medium text-leland-primary">
                    {isNewExperience ? 'Starting at ' : ''}
                    {formatPrice(
                      (isNewExperience
                        ? coachStartingPrice
                        : coach.hourlyRate) ?? 0,
                    )}
                    /hr
                  </span>
                </>
              )}
            </h2>
            <ServicesDescription
              className="text-leland-gray-light"
              services={services}
            />
          </div>
          <div className="mt-4 flex w-full items-center md:mt-0 md:w-auto">
            <Button
              label="Buy coaching"
              buttonColor={ButtonColor.WHITE}
              width={ButtonWidth.FULL_MOBILE}
              disabled={blocked}
              tooltipContent={blocked ? blockedUserCTAText : null}
              onClick={handleBuyCoaching}
              id="coach_offerings:buy_coaching"
            />
          </div>
        </div>
      ) : null}

      {showOtherCategoriesText ? (
        <OtherCategoriesText
          className="mt-6"
          coach={coach}
          excludeCategoryUrn={categoryUrn}
        />
      ) : null}

      {hasCollapsibleSection ? (
        <button
          className="link link--primary mt-6 flex items-center justify-start space-x-1.5 text-lg font-medium"
          onClick={() => setCollapsed((prevState) => !prevState)}
          id="coach_offerings:toggle_packages"
        >
          <p>
            {collapsed
              ? `View ${pluralize(
                  featuredPackages.length === gridSize &&
                    morePackages.length === 0
                    ? 1
                    : morePackages.length,
                  'more package',
                )}`
              : 'Collapse packages'}
          </p>
          <IconChevronDown
            className={`size-3 text-leland-primary transition-transform ${
              collapsed ? '' : 'rotate-180'
            }`}
          />
        </button>
      ) : null}

      {coach.lelandPlusContributor && isLelandPlusExistingCategory ? (
        <a
          href={
            isLelandPlusSubscriber
              ? LELAND_PLUS_REDIRECT_URL
              : getLandingPageUrl(coach.slug)
          }
          target="_blank"
          rel="noreferrer"
          id="coach_offerings:leland_plus"
        >
          <div className="mt-8 flex items-center justify-between rounded-lg border border-leland-gray-stroke p-4 shadow">
            <div className="flex items-center">
              <BrandLelandPlusIcon className="mr-4 size-10 rounded-full" />
              <div className="flex flex-col">
                <h2 className="text-lg font-medium">
                  {coach.user.firstName} is a contributor to Leland+
                </h2>
                <p className="text-leland-gray-light">
                  Access a library of videos, templates, and examples curated by
                  Leland&rsquo;s top coaches.
                </p>
              </div>
            </div>
            <div className="hidden sm:block">
              <Button
                label={
                  isLelandPlusSubscriber
                    ? 'View my Dashboard'
                    : 'Get started for free'
                }
                width={ButtonWidth.FULL_MOBILE}
              />
            </div>
          </div>
        </a>
      ) : null}

      <LoginModal
        onApplicantLogin={() => {
          setLoginModalOpen(false);
          if (CTAClicked === CTAType.CUSTOM_PACKAGE) {
            handleCustomPackageCTA();
          } else {
            handleBuyCoaching();
          }
        }}
      />

      <MessageModal
        coach={coach}
        categoryUrn={categoryUrn}
        defaultMessage={`Hi ${coach.user.firstName}! I'm interested in purchasing coaching from you and wanted to request a custom coaching package. Are you free to chat in the next couple of days about what I'm looking for?`}
      />
    </LazyMotion>
  );
};

export default CoachOfferings;
